import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { chargerUpdateBody, PLUG_TYPES } from '@pulsepoint/shared';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';
import { MapPin } from 'lucide-react';
import { useChargers, useUpdateCharger, type Charger } from '../../api/chargers';
import { useDrivers } from '../../api/drivers';
import { useSites } from '../../api/organisations';
import { siteLabels } from '../../lib/place';
import { usePermission } from '../../auth/permissions';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { applyApiError } from '../../components/form/apiErrors';
import { FieldRow, FormSection } from '../../components/form/FieldRow';
import { FormSelect } from '../../components/form/FormSelect';
import { FormTextField } from '../../components/form/FormTextField';
import { useToast } from '../../components/Toast';
import { MapPickerDialog, pointOf } from '../../components/map/MapPickerDialog';
import { formSaver, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { MAPTILER_KEY } from '../../lib/maptiler';
import { chargerName } from './status';

type In = z.input<typeof chargerUpdateBody>;
type Out = z.output<typeof chargerUpdateBody>;

const AUTH_OPTIONS = [
  { value: 'rfid', label: 'RFID card: drivers tap their card' },
  { value: 'plug_and_charge', label: 'Plug and charge: starts when a car plugs in' },
];
const PLUG_OPTIONS = PLUG_TYPES.map((p) => ({ value: p, label: p }));
/** The OCPP handling profiles in apps/ocpp/src/vendors. */
const PROFILES = [
  { value: 'auto', label: 'Automatic, from what the charger reports' },
  { value: 'schneider', label: 'Schneider' },
  { value: 'smappee', label: 'Smappee' },
  { value: 'homepoint', label: 'Homepoint (AEFA)' },
  { value: 'nohmad', label: 'Nohmad' },
  { value: 'base', label: 'Standard OCPP 1.6' },
];
const FIELDS = ['label', 'bay', 'latitude', 'longitude', 'siteId', 'authMethod', 'assignedDriverId', 'ratedKw', 'plugTypes', 'connectorCount', 'installedOn', 'notes', 'vendorProfile'];

/** Name, place, how drivers start a session, and the numbers reports use. (Public charging is on the Pricing tab.) */
export function SettingsTab({ charger: c }: { charger: Charger }) {
  const canEdit = usePermission('chargers.edit');
  const canConfigure = usePermission('chargers.configure');
  const sites = useSites(undefined);
  const update = useUpdateCharger();
  const toast = useToast();
  const dialog = useDialog();
  const allChargers = useChargers();
  const [error, setError] = useState('');
  const readOnly = !canEdit || c.registration !== 'accepted';

  const form = useForm<In, unknown, Out>({
    resolver: zodResolver(chargerUpdateBody),
    disabled: readOnly,
    values: {
      label: c.label ?? '',
      bay: c.bay ?? '',
      latitude: c.latitude ?? '',
      longitude: c.longitude ?? '',
      siteId: c.siteId ?? '',
      authMethod: c.authMethod,
      assignedDriverId: c.assignedDriverId ?? '',
      ratedKw: c.ratedKw ? String(Number(c.ratedKw)) : '',
      plugTypes: c.plugTypes.length ? c.plugTypes : ['Type 2'],
      connectorCount: String(c.connectorCount),
      installedOn: c.installedOn ?? '',
      notes: c.notes ?? '',
      vendorProfile: c.vendorProfile ?? 'auto',
    } as unknown as In,
    resetOptions: { keepDirtyValues: true }, // live updates don't wipe what you're typing
  });

  // A dropdown per connector (1 to 8; anything else while typing shows one).
  const typedCount = Number(useWatch({ control: form.control, name: 'connectorCount' }));
  const connectors = Number.isInteger(typedCount) && typedCount >= 1 && typedCount <= 8 ? typedCount : 1;

  const submit = formSaver(form, async (v) => {
    setError('');
    const { vendorProfile, ...rest } = v;
    // One plug per connector: new connectors get the first one's plug.
    const plugTypes = Array.from({ length: v.connectorCount ?? connectors }, (_, i) => v.plugTypes?.[i] ?? v.plugTypes?.[0] ?? 'Type 2');
    try {
      await update.mutateAsync({ id: c.id, ...rest, plugTypes, ...(canConfigure ? { vendorProfile } : {}) });
      form.reset(undefined, { keepValues: true });
      toast('The charger settings were saved.');
    } catch (e) {
      setError(applyApiError(e, form.setError, FIELDS));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset(), tab: true });

  // Plug and charge bills one driver, from the organisation of the site it's on.
  const plugAndCharge = form.watch('authMethod') === 'plug_and_charge';
  const chosenSite = (sites.data ?? []).find((s) => s.id === form.watch('siteId'));
  const siteOrg = chosenSite?.organisationId ?? c.organisationId;
  const siteOrgName = chosenSite?.organisationName ?? c.organisationName;
  const drivers = useDrivers('active', siteOrg ?? undefined, plugAndCharge && !!siteOrg);
  const driverOptions = (drivers.data ?? [])
    .filter((d) => !d.needsReview)
    .map((d) => ({ value: d.id, label: d.vehicleType ? `${d.name} (${d.vehicleType})` : d.name }));
  if (c.assignedDriverId && c.organisationId === siteOrg && !driverOptions.some((o) => o.value === c.assignedDriverId)) {
    driverOptions.unshift({ value: c.assignedDriverId, label: c.assignedDriverName ?? 'Current driver' });
  }

  // Its own spot, picked on a map: starts on its pin, else the site's; the site's other placed chargers show as grey dots.
  const pickOnMap = async () => {
    const v = form.getValues();
    const site = chosenSite ?? (sites.data ?? []).find((s) => s.id === c.siteId);
    const landmarks = (allChargers.data ?? [])
      .filter((o) => o.id !== c.id && o.siteId === (v.siteId || c.siteId))
      .flatMap((o) => {
        const p = pointOf(o.latitude, o.longitude);
        return p ? [{ ...p, label: chargerName(o) }] : [];
      });
    const picked = await dialog.open(MapPickerDialog, {
      title: `Where ${chargerName(c)} is`,
      lead: 'Click the map where the charger stands, then drag the pin to adjust. Grey dots are the site’s other chargers.',
      value: pointOf(v.latitude, v.longitude),
      centre: pointOf(site?.latitude, site?.longitude),
      landmarks,
    });
    if (!picked) return;
    form.setValue('latitude', picked.latitude, { shouldDirty: true, shouldValidate: true });
    form.setValue('longitude', picked.longitude, { shouldDirty: true, shouldValidate: true });
  };

  const usable = (sites.data ?? []).filter((s) => !c.organisationId || s.organisationId === c.organisationId || canConfigure);
  const labels = siteLabels(usable, usable.some((s) => s.organisationId !== c.organisationId));
  const siteOptions = usable.map((s) => ({ value: s.id, label: labels.get(s.id) ?? s.name })).sort((a, b) => a.label.localeCompare(b.label, 'en-AU'));

  return (
    <Box component="form" onSubmit={submit} noValidate sx={{ display: 'grid', gap: 4 }}>
      <FormSection title="Where it is">
        <FieldRow>
          <FormTextField control={form.control} name="label" label="Name" placeholder={c.chargePointId} helperText="Shown instead of the charge point ID" />
          <FormTextField control={form.control} name="bay" label="Bay" placeholder="e.g. 3" />
        </FieldRow>
        {/* Only worth showing when there's somewhere else it could be. */}
        {siteOptions.length > 1 && <FormSelect control={form.control} name="siteId" label="Where it is" options={siteOptions} />}
        <FieldRow columns="1fr 1fr auto">
          <FormTextField control={form.control} name="latitude" label="Latitude" placeholder="-33.3936" helperText="Its own spot on the map. Blank: the site’s location" slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
          <FormTextField control={form.control} name="longitude" label="Longitude" placeholder="151.3240" slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
          {!readOnly && MAPTILER_KEY && (
            <Button startIcon={<MapPin size={16} strokeWidth={1.75} />} onClick={() => void pickOnMap()} sx={{ alignSelf: 'start', minHeight: 0, height: 'calc(1.4375rem + 24px)', whiteSpace: 'nowrap' }}>
              Pick on the map
            </Button>
          )}
        </FieldRow>
      </FormSection>

      <FormSection title="How sessions start">
        <FormSelect control={form.control} name="authMethod" label="Drivers start a session by" options={AUTH_OPTIONS} />
        {plugAndCharge && (
          <FormSelect
            control={form.control}
            name="assignedDriverId"
            label="Driver"
            options={driverOptions}
            emptyLabel="Nobody yet"
            helperText={driverOptions.length ? 'Every session here is billed to this driver, e.g. their home charger' : `Add the driver to ${siteOrgName ?? 'this organisation'} on the Drivers page first`}
          />
        )}
      </FormSection>

      <FormSection title="Details">
        <FieldRow columns="1fr 1fr 1fr">
          <FormTextField control={form.control} name="ratedKw" label="Rated power (kW)" slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
          <FormTextField control={form.control} name="connectorCount" label="Connectors" slotProps={{ htmlInput: { inputMode: 'numeric' } }} />
          {connectors === 1 && <FormSelect control={form.control} name="plugTypes.0" label="Plug type" options={PLUG_OPTIONS} />}
        </FieldRow>
        {connectors > 1 && (
          <FieldRow columns={`repeat(${Math.min(connectors, 4)}, 1fr)`}>
            {Array.from({ length: connectors }, (_, i) => (
              <FormSelect key={i} control={form.control} name={`plugTypes.${i}`} label={`Connector ${String.fromCharCode(65 + i)} plug`} options={PLUG_OPTIONS} />
            ))}
          </FieldRow>
        )}
        <FieldRow>
          <FormTextField control={form.control} name="installedOn" label="Installed" type="date" slotProps={{ inputLabel: { shrink: true } }} />
        </FieldRow>
        {canConfigure && <FormSelect control={form.control} name="vendorProfile" label="OCPP handling" options={PROFILES} helperText="Change only if the charger is misread" />}
        <FormTextField control={form.control} name="notes" label="Notes" multiline minRows={2} />
      </FormSection>

      {error && <Alert severity="error" role="alert">{error}</Alert>}
      {!readOnly && (
        <Box>
          <Button type="submit" variant="contained" disabled={update.isPending || !form.formState.isDirty}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
